import type { HoverResetProgressScope } from "@/features/hoverIdle/core/hoverResetPlans";
import type { NHovBranchId } from "@/features/hoverIdle/types";
import { OHOV_UNLOCK_HOV_THRESHOLD } from "@/features/hoverIdle/oHov/oHovLogic";
import { WALL_DEFINITIONS, type WallId } from "./wallDefinitions";
import {
  STEP_WALL_BLOCKED_CELL_INDICES,
  resolveStepWallRewardEffectiveMaxMultiplier,
  resolveStepWallRewardMultiplier,
} from "./stepWallLogic";
import {
  resolvePriceWallRewardEffectiveMaxMultiplier,
  resolvePriceWallRewardMultiplier,
} from "./priceWallLogic";
import {
  resolveMpWallRewardEffectiveMaxMpBonus,
  resolveMpWallRewardMpBonus,
} from "./mpWallLogic";
import {
  resolveFreshnessWallRewardEffectiveMaxExponentBonus,
  resolveFreshnessWallRewardExponentBonus,
} from "./freshnessWallLogic";
import {
  OUTER_WALL_CLEAR_TARGET_HOV,
  resolveOuterWallBuiltInOHovAutoTarget,
  resolveOuterWallBuiltInOHovAutoTargetEffectiveMaxTier,
} from "./outerWallLogic";
import {
  SPLIT_WALL_CLEAR_TARGET_HOV,
  resolveSplitWallH1GRewardEffectiveMaxMultiplier,
  resolveSplitWallH1GRewardMultiplier,
} from "./splitWallLogic";
import {
  resolveHgWallRewardEffectiveMaxExponentBonus,
  resolveHgWallRewardExponentBonus,
} from "./hgWallLogic";
import {
  resolveStockWallEffectiveNhovExecutionLimit,
  resolveStockWallRewardEffectiveMaxNhovLimitBonus,
  resolveStockWallRewardNhovLimitBonus,
} from "./stockWallLogic";
import {
  AMPLIFICATION_WALL_CORRUPTED_SOURCE_GAIN_MULTIPLIER,
  resolveAmplificationWallRewardInfluence,
} from "./amplificationWallLogic";
import {
  DEFAULT_WALL_REWARD_CAP_LEVEL,
  resolveWallRewardEffectiveCap,
  type WallRewardCapLevel,
  type WallRewardEffectiveCap,
} from "./wallRewardCap";
import type { WallState } from "./wallState";

// Typed reward effect produced by a Wall's clear record. Each Wall declares
// the downstream dimension it composes into, plus the operation (`multiply`
// for product aggregation, `add` for additive aggregation, or a typed
// dimension-specific operation). The pair is fixed per dimension so aggregate
// helpers can rely on the discriminator:
//   - hov-gain                     : multiply (Hov gain pipeline)
//   - hov1-gain                    : multiply (H1G / Hov Cell 1 gain only)
//   - simultaneous-purchase-count  : multiply (HG / HnG planner cap)
//   - mp-capacity                  : add      (MP capacity bonus)
//   - mhov-reward-exponent-bonus   : add      (MHov reward exponent)
//   - bhg-to-hg-power-exponent-bonus : add    (BHG -> HG power exponent)
//   - stock-wall-nhov-limit        : add      (Stock Wall NHov execution limit)
//   - ohov-built-in-auto-target    : replace-target (built-in OHov auto target; not OHov MP auto)
//   - amplifier-gain-ra-propagation-influence : propagate-ra-bonus
//     (influence applied to the pre-Wall9 RA external multiplier bonus)
// Multiply and add effects must not be cross-aggregated; a "1 + bonus" display
// value is not a real multiplier and must not flow into multiplier aggregates.
export type WallRewardEffect =
  | {
      readonly dimension: "hov-gain";
      readonly operation: "multiply";
      readonly value: number;
    }
  | {
      readonly dimension: "hov1-gain";
      readonly operation: "multiply";
      readonly value: number;
    }
  | {
      readonly dimension: "simultaneous-purchase-count";
      readonly operation: "multiply";
      readonly value: number;
    }
  | {
      readonly dimension: "mp-capacity";
      readonly operation: "add";
      readonly value: number;
    }
  | {
      readonly dimension: "mhov-reward-exponent-bonus";
      readonly operation: "add";
      readonly value: number;
    }
  | {
      readonly dimension: "bhg-to-hg-power-exponent-bonus";
      readonly operation: "add";
      readonly value: number;
    }
  | {
      readonly dimension: "stock-wall-nhov-limit";
      readonly operation: "add";
      readonly value: number;
    }
  | {
      readonly dimension: "ohov-built-in-auto-target";
      readonly operation: "replace-target";
      readonly tier: number;
      readonly builtInOHovAutoTargetHov: string;
    }
  | ({
      readonly dimension: "amplifier-gain-ra-propagation-influence";
      readonly operation: "propagate-ra-bonus";
    } & (
      | { readonly acquired: false }
      | { readonly acquired: true; readonly value: number }
    ));

export type WallRewardDimension = WallRewardEffect["dimension"];

export type WallAmplifierGainRaPropagationRewardEffect = Extract<
  WallRewardEffect,
  { readonly dimension: "amplifier-gain-ra-propagation-influence" }
>;

export type WallRewardMultiplyDimension = Extract<
  WallRewardEffect,
  { readonly operation: "multiply" }
>["dimension"];

export type WallRewardAddDimension = Extract<
  WallRewardEffect,
  { readonly operation: "add" }
>["dimension"];

export interface WallAmplifierChallenge {
  readonly kind: "corrupted-unavailable-amplifiers";
  readonly lowerSourceGainMultiplierPerCorruptedEntry: number;
}

// Per-Wall pure policy boundary. The model owns authoritative state and
// orchestrates command-level lifecycle (start / clear / mark seen); each
// policy answers the wall-specific questions the model needs to apply that
// orchestration:
//   - which reset scope to use at start
//   - whether this wall is currently challenge-available (unlock gate)
//   - which Hov cells the active wall blocks
//   - which NHov branches the active wall blocks
//   - whether target-specific direct MP is blocked while this wall is active
//   - how a finished run becomes a clear record on the per-wall slice
//   - what typed reward effect the wall contributes
// Policies must not touch model internals or produce side effects.

export interface WallChallengeClearRecord {
  readonly clearElapsedMs: number;
  readonly previousBestClearMs: number | null;
  readonly nextBestClearMs: number;
}

export interface ResolveWallClearRecordInput {
  readonly state: WallState;
  // Accumulated game-time elapsed for the active wall run. Already
  // game-speed-adjusted by the model, so policies must not mix wall-clock
  // timestamps here.
  readonly elapsedMs: number;
}

export interface ApplyWallClearRecordInput {
  readonly state: WallState;
  readonly clearRecord: WallChallengeClearRecord;
}

export interface WallChallengePolicy {
  readonly id: WallId;
  readonly startResetScope: HoverResetProgressScope;
  readonly clearTargetHov: string;
  isChallengeAvailable(state: WallState): boolean;
  isHovCellUnlockBlocked(cellIndex: number): boolean;
  isNHovBranchBlocked(branchId: NHovBranchId): boolean;
  readonly hovDirectMpMode: "mpm" | "wall-slots";
  // When true, the second Hov grid increase (tier 9) is suppressed while this
  // wall is active so the effective max grid tier stays at the first grid
  // increase (tier 4). Only constrains tier application while active; the
  // unlock latch and all non-active behavior are unaffected.
  readonly blocksSecondHovGridIncrease: boolean;
  readonly amplifierChallenge?: WallAmplifierChallenge;
  resolveBestClearMs(state: WallState): number | null;
  // Returns the authoritative typed reward effect this wall contributes.
  // Aggregate helpers and snapshot/PlayLog/UI surfaces should consume this
  // rather than the legacy `resolveRewardMultiplier` shim.
  resolveRewardEffect(
    state: WallState,
    capLevel?: WallRewardCapLevel,
  ): WallRewardEffect;
  // Effective upper bound currently applied to this wall's reward value, or
  // null when the reward has no upper bound at all. Resolved from the same
  // effective max the reward clamp uses, so cap-reached state follows any
  // future relaxation instead of a fixed per-wall constant.
  resolveRewardEffectiveCap(
    state: WallState,
    capLevel?: WallRewardCapLevel,
  ): WallRewardEffectiveCap | null;
  // Compatibility shim for callers that only need the multiplier value of a
  // `multiply`-operation effect. Returns 1 for non-multiply effects; add
  // effects must NEVER be folded into multiplier callers.
  resolveRewardMultiplier(
    state: WallState,
    capLevel?: WallRewardCapLevel,
  ): number;
  hasClearRecord(state: WallState): boolean;
  resolveClearRecord(
    input: ResolveWallClearRecordInput,
  ): WallChallengeClearRecord;
  applyClearRecord(input: ApplyWallClearRecordInput): WallState;
}

function rewardMultiplierFromEffect(effect: WallRewardEffect): number {
  return effect.operation === "multiply" ? effect.value : 1;
}

const STEP_WALL_START_RESET_SCOPE: HoverResetProgressScope = {
  hovResource: "reset-to-zero",
  hiddenHovGainBonus: "reset-to-zero",
  purchasedHovGainUpgrades: "reset-to-zero",
  hovUpgradeCost: "reset-to-base",
  timUpgradeCost: "preserve",
  hovTargetGrid: "reset-to-default",
  hovGridThemeHue: "clear",
  mHovState: "reset-to-default",
  nHovState: "reset-to-default",
  secretProgressTimers: "reset-to-zero",
  hovAmplifier: "reset-gauge-and-ap-preserve-enhancement",
};

export const STEP_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "step-wall",
  startResetScope: STEP_WALL_START_RESET_SCOPE,
  clearTargetHov: OHOV_UNLOCK_HOV_THRESHOLD,
  // Step Wall is the first Wall in the progression; it is challenge-available
  // as soon as the Wall surface itself is unlocked.
  isChallengeAvailable: () => true,
  isHovCellUnlockBlocked: (cellIndex) =>
    STEP_WALL_BLOCKED_CELL_INDICES.includes(cellIndex),
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: false,
  resolveBestClearMs: (state) => state.stepWallBestClearMs,
  resolveRewardEffect: (state, capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL) => ({
    dimension: "hov-gain",
    operation: "multiply",
    value: resolveStepWallRewardMultiplier(
      state.stepWallBestClearMs,
      resolveStepWallRewardEffectiveMaxMultiplier(capLevel),
    ),
  }),
  resolveRewardEffectiveCap: (
    state,
    capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
  ) =>
    resolveWallRewardEffectiveCap(
      resolveStepWallRewardMultiplier(
        state.stepWallBestClearMs,
        resolveStepWallRewardEffectiveMaxMultiplier(capLevel),
      ),
      resolveStepWallRewardEffectiveMaxMultiplier(capLevel),
    ),
  resolveRewardMultiplier(state, capLevel) {
    return rewardMultiplierFromEffect(
      this.resolveRewardEffect(state, capLevel),
    );
  },
  hasClearRecord: (state) => state.stepWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.stepWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    stepWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

// Price Wall — Wall2. Available once Step Wall has been cleared at least once.
// Reward composes into the HG / HnG planner's simultaneous purchase count cap
// only (dimension = "simultaneous-purchase-count"); it must not leak into the
// Hov gain pipeline's `wallRewardMultiplier`.
const PRICE_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

const PRICE_WALL_BLOCKED_NHOV_BRANCH_IDS: ReadonlySet<NHovBranchId> = new Set([
  "hgDiscount",
]);

export const PRICE_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "price-wall",
  startResetScope: PRICE_WALL_START_RESET_SCOPE,
  clearTargetHov: OHOV_UNLOCK_HOV_THRESHOLD,
  // Price Wall unlocks after Step Wall has been cleared at least once.
  isChallengeAvailable: (state) => state.stepWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: (branchId) =>
    PRICE_WALL_BLOCKED_NHOV_BRANCH_IDS.has(branchId),
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: false,
  resolveBestClearMs: (state) => state.priceWallBestClearMs,
  resolveRewardEffect: (state, capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL) => ({
    dimension: "simultaneous-purchase-count",
    operation: "multiply",
    value: resolvePriceWallRewardMultiplier(
      state.priceWallBestClearMs,
      resolvePriceWallRewardEffectiveMaxMultiplier(capLevel),
    ),
  }),
  resolveRewardEffectiveCap: (
    state,
    capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
  ) =>
    resolveWallRewardEffectiveCap(
      resolvePriceWallRewardMultiplier(
        state.priceWallBestClearMs,
        resolvePriceWallRewardEffectiveMaxMultiplier(capLevel),
      ),
      resolvePriceWallRewardEffectiveMaxMultiplier(capLevel),
    ),
  resolveRewardMultiplier(state, capLevel) {
    return rewardMultiplierFromEffect(
      this.resolveRewardEffect(state, capLevel),
    );
  },
  hasClearRecord: (state) => state.priceWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.priceWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    priceWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

// MP Wall - Wall4. Available once Freshness Wall has been cleared at least
// once.
// Clear reward composes additively into the authoritative MP capacity via the
// `mp-capacity` add effect; the same value is exposed through
// `resolveAggregateWallRewardAddend(state, "mp-capacity")` so MP capacity
// composition, MPM snapshot, and PlayLog all read the same authoritative
// bonus. The active challenge blocks target-specific direct MP effects
// without mutating the assignment state.
const MP_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

export const MP_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "mp-wall",
  startResetScope: MP_WALL_START_RESET_SCOPE,
  clearTargetHov: OHOV_UNLOCK_HOV_THRESHOLD,
  isChallengeAvailable: (state) => state.freshnessWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "wall-slots",
  blocksSecondHovGridIncrease: false,
  resolveBestClearMs: (state) => state.mpWallBestClearMs,
  resolveRewardEffect: (state, capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL) => ({
    dimension: "mp-capacity",
    operation: "add",
    value: resolveMpWallRewardMpBonus(
      state.mpWallBestClearMs,
      resolveMpWallRewardEffectiveMaxMpBonus(capLevel),
    ),
  }),
  resolveRewardEffectiveCap: (
    state,
    capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
  ) =>
    resolveWallRewardEffectiveCap(
      resolveMpWallRewardMpBonus(
        state.mpWallBestClearMs,
        resolveMpWallRewardEffectiveMaxMpBonus(capLevel),
      ),
      resolveMpWallRewardEffectiveMaxMpBonus(capLevel),
    ),
  resolveRewardMultiplier(state, capLevel) {
    return rewardMultiplierFromEffect(
      this.resolveRewardEffect(state, capLevel),
    );
  },
  hasClearRecord: (state) => state.mpWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.mpWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    mpWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

// Freshness Wall — Wall3. Available once Price Wall has been cleared at least
// once. The challenge modifier (MHov preview/execution staling) lives at the
// model layer because it crosses the MHov/HugeNumber boundary; this policy
// owns lifecycle (start scope, best-clear bookkeeping) only. The clear reward
// composes additively into MHOV_REWARD_EXPONENT via the typed `add` effect on
// the `mhov-reward-exponent-bonus` dimension; the model reads the same value
// through `getFreshnessWallRewardExponentBonus(state)` to keep preview,
// manual execution, and MP-gate execution on a single authoritative path.
const FRESHNESS_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

export const FRESHNESS_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "freshness-wall",
  startResetScope: FRESHNESS_WALL_START_RESET_SCOPE,
  clearTargetHov: OHOV_UNLOCK_HOV_THRESHOLD,
  isChallengeAvailable: (state) => state.priceWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: false,
  resolveBestClearMs: (state) => state.freshnessWallBestClearMs,
  resolveRewardEffect: (state, capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL) => ({
    dimension: "mhov-reward-exponent-bonus",
    operation: "add",
    value: resolveFreshnessWallRewardExponentBonus(
      state.freshnessWallBestClearMs,
      resolveFreshnessWallRewardEffectiveMaxExponentBonus(capLevel),
    ),
  }),
  resolveRewardEffectiveCap: (
    state,
    capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
  ) =>
    resolveWallRewardEffectiveCap(
      resolveFreshnessWallRewardExponentBonus(
        state.freshnessWallBestClearMs,
        resolveFreshnessWallRewardEffectiveMaxExponentBonus(capLevel),
      ),
      resolveFreshnessWallRewardEffectiveMaxExponentBonus(capLevel),
    ),
  resolveRewardMultiplier(state, capLevel) {
    return rewardMultiplierFromEffect(
      this.resolveRewardEffect(state, capLevel),
    );
  },
  hasClearRecord: (state) => state.freshnessWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.freshnessWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    freshnessWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

// Outer Wall - Wall5. Available once MP Wall has been cleared at least
// once. Unlike earlier walls, its clear condition is explicit: the wall clears
// when the active OHov boundary reaches its higher Hov target. Its first clear
// unlocks the OHov MP assignment / execution-condition form (not built-in automation);
// repeat clears raise the built-in OHov auto execution target (the value that
// replaces 1.8e308 Hov), which is independent of the OHov MP auto conditions.
const OUTER_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

export const OUTER_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "outer-wall",
  startResetScope: OUTER_WALL_START_RESET_SCOPE,
  clearTargetHov: OUTER_WALL_CLEAR_TARGET_HOV,
  isChallengeAvailable: (state) => state.mpWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: false,
  resolveBestClearMs: (state) => state.outerWallBestClearMs,
  resolveRewardEffect: (state, capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL) => {
    const { tier, builtInOHovAutoTargetHov } =
      resolveOuterWallBuiltInOHovAutoTarget(
        state.outerWallBestClearMs,
        capLevel,
      );
    return {
      dimension: "ohov-built-in-auto-target",
      operation: "replace-target",
      tier,
      builtInOHovAutoTargetHov,
    };
  },
  // Outer Wall's reward is bounded on its target tier, not on the formatted
  // Hov target string the tier expands to.
  resolveRewardEffectiveCap: (
    state,
    capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
  ) =>
    resolveWallRewardEffectiveCap(
      resolveOuterWallBuiltInOHovAutoTarget(
        state.outerWallBestClearMs,
        capLevel,
      ).tier,
      resolveOuterWallBuiltInOHovAutoTargetEffectiveMaxTier(capLevel),
    ),
  resolveRewardMultiplier(state, capLevel) {
    return rewardMultiplierFromEffect(
      this.resolveRewardEffect(state, capLevel),
    );
  },
  hasClearRecord: (state) => state.outerWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.outerWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    outerWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

// Split Wall — Wall6. Available once Outer Wall has been cleared at least once.
// While active it caps the Hov grid at the first grid increase (tier 4) by
// suppressing the second grid increase (tier 9) via
// `blocksSecondHovGridIncrease`. The clear reward multiplies H1G (Hov Cell 1
// gain only) through the typed `multiply` effect on the `hov1-gain` dimension;
// the model reads the same value via
// `resolveAggregateWallRewardMultiplier(state, "hov1-gain")` so the H1G cell
// gain composes it alongside other H1G multipliers.
const SPLIT_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

export const SPLIT_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "split-wall",
  startResetScope: SPLIT_WALL_START_RESET_SCOPE,
  clearTargetHov: SPLIT_WALL_CLEAR_TARGET_HOV,
  isChallengeAvailable: (state) => state.outerWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: true,
  resolveBestClearMs: (state) => state.splitWallBestClearMs,
  resolveRewardEffect: (state, capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL) => ({
    dimension: "hov1-gain",
    operation: "multiply",
    value: resolveSplitWallH1GRewardMultiplier(
      state.splitWallBestClearMs,
      resolveSplitWallH1GRewardEffectiveMaxMultiplier(capLevel),
    ),
  }),
  resolveRewardEffectiveCap: (
    state,
    capLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
  ) =>
    resolveWallRewardEffectiveCap(
      resolveSplitWallH1GRewardMultiplier(
        state.splitWallBestClearMs,
        resolveSplitWallH1GRewardEffectiveMaxMultiplier(capLevel),
      ),
      resolveSplitWallH1GRewardEffectiveMaxMultiplier(capLevel),
    ),
  resolveRewardMultiplier(state, capLevel) {
    return rewardMultiplierFromEffect(
      this.resolveRewardEffect(state, capLevel),
    );
  },
  hasClearRecord: (state) => state.splitWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.splitWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    splitWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

// HG Wall — Wall7. Available once Split Wall has been cleared at least once.
// While active it weakens HG via the HG-Wall-specific divisor/exponent
// constraint, applied at the model layer because it crosses the HG/HugeNumber
// boundary; this policy owns lifecycle (best-clear bookkeeping) only. The clear
// reward composes additively into the BHG -> HG power exponent via the typed
// `add` effect on the `bhg-to-hg-power-exponent-bonus` dimension; the model
// reads the same value through `getHgWallRewardExponentBonus(state)` so the
// reward stacks with the skill-side `hov-hg-exponent-bonus` on a single
// authoritative exponent.
const HG_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

export const HG_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "hg-wall",
  startResetScope: HG_WALL_START_RESET_SCOPE,
  clearTargetHov: OHOV_UNLOCK_HOV_THRESHOLD,
  isChallengeAvailable: (state) => state.splitWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: false,
  resolveBestClearMs: (state) => state.hgWallBestClearMs,
  resolveRewardEffect: (state) => ({
    dimension: "bhg-to-hg-power-exponent-bonus",
    operation: "add",
    value: resolveHgWallRewardExponentBonus(state.hgWallBestClearMs),
  }),
  resolveRewardEffectiveCap: (state) =>
    resolveWallRewardEffectiveCap(
      resolveHgWallRewardExponentBonus(state.hgWallBestClearMs),
      resolveHgWallRewardEffectiveMaxExponentBonus(),
    ),
  resolveRewardMultiplier(state) {
    return rewardMultiplierFromEffect(this.resolveRewardEffect(state));
  },
  hasClearRecord: (state) => state.hgWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.hgWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    hgWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

const STOCK_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

// Stock Wall — Wall8. Available once HG Wall has been cleared. Branch-level
// policy remains unchanged; the active-run shared inventory is resolved from
// WallState so every NHov branch and execution source consumes one stock.
export const STOCK_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "stock-wall",
  startResetScope: STOCK_WALL_START_RESET_SCOPE,
  clearTargetHov: OHOV_UNLOCK_HOV_THRESHOLD,
  isChallengeAvailable: (state) => state.hgWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: false,
  resolveBestClearMs: (state) => state.stockWallBestClearMs,
  resolveRewardEffect: (state) => ({
    dimension: "stock-wall-nhov-limit",
    operation: "add",
    value: resolveStockWallRewardNhovLimitBonus(state.stockWallBestClearMs),
  }),
  resolveRewardEffectiveCap: (state) =>
    resolveWallRewardEffectiveCap(
      resolveStockWallRewardNhovLimitBonus(state.stockWallBestClearMs),
      resolveStockWallRewardEffectiveMaxNhovLimitBonus(),
    ),
  resolveRewardMultiplier(state) {
    return rewardMultiplierFromEffect(this.resolveRewardEffect(state));
  },
  hasClearRecord: (state) => state.stockWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.stockWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    stockWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

const AMPLIFICATION_WALL_START_RESET_SCOPE: HoverResetProgressScope =
  STEP_WALL_START_RESET_SCOPE;

export const AMPLIFICATION_WALL_CHALLENGE_POLICY: WallChallengePolicy = {
  id: "amplification-wall",
  startResetScope: AMPLIFICATION_WALL_START_RESET_SCOPE,
  clearTargetHov: OHOV_UNLOCK_HOV_THRESHOLD,
  isChallengeAvailable: (state) => state.stockWallBestClearMs !== null,
  isHovCellUnlockBlocked: () => false,
  isNHovBranchBlocked: () => false,
  hovDirectMpMode: "mpm",
  blocksSecondHovGridIncrease: false,
  amplifierChallenge: {
    kind: "corrupted-unavailable-amplifiers",
    lowerSourceGainMultiplierPerCorruptedEntry:
      AMPLIFICATION_WALL_CORRUPTED_SOURCE_GAIN_MULTIPLIER,
  },
  resolveBestClearMs: (state) => state.amplificationWallBestClearMs,
  resolveRewardEffect: (state) => {
    const value = resolveAmplificationWallRewardInfluence(
      state.amplificationWallBestClearMs,
    );
    return value === null
      ? {
          dimension: "amplifier-gain-ra-propagation-influence",
          operation: "propagate-ra-bonus",
          acquired: false,
        }
      : {
          dimension: "amplifier-gain-ra-propagation-influence",
          operation: "propagate-ra-bonus",
          acquired: true,
          value,
        };
  },
  resolveRewardEffectiveCap: () => null,
  resolveRewardMultiplier: () => 1,
  hasClearRecord: (state) => state.amplificationWallBestClearMs !== null,
  resolveClearRecord: ({ state, elapsedMs }) => {
    const clearElapsedMs = Math.max(0, elapsedMs);
    const previousBestClearMs = state.amplificationWallBestClearMs;
    const nextBestClearMs =
      previousBestClearMs === null || clearElapsedMs < previousBestClearMs
        ? clearElapsedMs
        : previousBestClearMs;
    return { clearElapsedMs, previousBestClearMs, nextBestClearMs };
  },
  applyClearRecord: ({ state, clearRecord }) => ({
    ...state,
    amplificationWallBestClearMs: clearRecord.nextBestClearMs,
  }),
};

const POLICY_BY_ID: Readonly<Record<WallId, WallChallengePolicy>> = {
  "step-wall": STEP_WALL_CHALLENGE_POLICY,
  "price-wall": PRICE_WALL_CHALLENGE_POLICY,
  "freshness-wall": FRESHNESS_WALL_CHALLENGE_POLICY,
  "mp-wall": MP_WALL_CHALLENGE_POLICY,
  "outer-wall": OUTER_WALL_CHALLENGE_POLICY,
  "split-wall": SPLIT_WALL_CHALLENGE_POLICY,
  "hg-wall": HG_WALL_CHALLENGE_POLICY,
  "stock-wall": STOCK_WALL_CHALLENGE_POLICY,
  "amplification-wall": AMPLIFICATION_WALL_CHALLENGE_POLICY,
};

export const WALL_CHALLENGE_POLICIES: readonly WallChallengePolicy[] =
  WALL_DEFINITIONS.map((definition) => POLICY_BY_ID[definition.id]);

export function getWallChallengePolicy(wallId: WallId): WallChallengePolicy {
  return POLICY_BY_ID[wallId];
}

export function resolveActiveWallClearTargetHov(state: WallState): string {
  if (state.activeWallId === null) return OHOV_UNLOCK_HOV_THRESHOLD;
  return getWallChallengePolicy(state.activeWallId).clearTargetHov;
}

export function resolveOuterWallBuiltInOHovAutoTargetHov(
  state: WallState,
  capLevel: WallRewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
): string | null {
  if (state.outerWallBestClearMs === null) return null;
  const effect = OUTER_WALL_CHALLENGE_POLICY.resolveRewardEffect(
    state,
    capLevel,
  );
  return effect.operation === "replace-target"
    ? effect.builtInOHovAutoTargetHov
    : null;
}

export function isHovCellUnlockBlockedByActiveWall(
  state: WallState,
  cellIndex: number,
): boolean {
  return resolveHovCellUnlockBlockedWallId(state, cellIndex) !== null;
}

export function resolveHovCellUnlockBlockedWallId(
  state: WallState,
  cellIndex: number,
): WallId | null {
  if (state.activeWallId === null) return null;
  const policy = getWallChallengePolicy(state.activeWallId);
  return policy.isHovCellUnlockBlocked(cellIndex) ? policy.id : null;
}

export function isNHovBranchBlockedByActiveWall(
  state: WallState,
  branchId: NHovBranchId,
): boolean {
  if (state.activeWallId === null) return false;
  if (isStockWallNhovExecutionDepleted(state)) return true;
  return getWallChallengePolicy(state.activeWallId).isNHovBranchBlocked(
    branchId,
  );
}

export function isHovTargetDirectMpBlockedByActiveWall(
  state: WallState,
): boolean {
  return resolveHovDirectMpMode(state) !== "mpm";
}

export function isHovGainUpgradeDirectMpBlockedByActiveWall(
  state: WallState,
): boolean {
  return resolveHovDirectMpMode(state) !== "mpm";
}

export function resolveHovDirectMpMode(
  state: WallState,
): WallChallengePolicy["hovDirectMpMode"] {
  return state.activeWallId === null
    ? "mpm"
    : getWallChallengePolicy(state.activeWallId).hovDirectMpMode;
}

export function isSecondHovGridIncreaseBlockedByActiveWall(
  state: WallState,
): boolean {
  if (state.activeWallId === null) return false;
  return getWallChallengePolicy(state.activeWallId).blocksSecondHovGridIncrease;
}

// Aggregate reward multiplier composed across walls of the requested multiply
// dimension. Walls whose effect is not `multiply` or whose dimension does not
// match are skipped, so `add` effects never leak into multiplier aggregates.
// A wall with no clear record contributes 1 by its own policy.
export function resolveAggregateWallRewardMultiplier(
  state: WallState,
  dimension: WallRewardMultiplyDimension,
  capLevel: WallRewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
): number {
  let multiplier = 1;
  for (const policy of WALL_CHALLENGE_POLICIES) {
    const effect = policy.resolveRewardEffect(state, capLevel);
    if (effect.operation !== "multiply") continue;
    if (effect.dimension !== dimension) continue;
    multiplier *= effect.value;
  }
  return multiplier;
}

// Aggregate reward addend composed across walls of the requested add
// dimension. Mirrors `resolveAggregateWallRewardMultiplier` but for additive
// effects, so multiply effects never leak into addend aggregates.
export function resolveAggregateWallRewardAddend(
  state: WallState,
  dimension: WallRewardAddDimension,
  capLevel: WallRewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
): number {
  let total = 0;
  for (const policy of WALL_CHALLENGE_POLICIES) {
    const effect = policy.resolveRewardEffect(state, capLevel);
    if (effect.operation !== "add") continue;
    if (effect.dimension !== dimension) continue;
    total += effect.value;
  }
  return total;
}

export function isFreshnessWallActive(state: WallState): boolean {
  return state.activeWallId === "freshness-wall";
}

// Effective Hov-run staling elapsed for the active Freshness Wall run. Returns
// 0 when no freshness staling is in effect (e.g. wall inactive, or wall just
// started). Cap at `Number.MAX_VALUE` so a paused run that overflows the
// counter still drives staling to its floor without producing NaN downstream.
export function getFreshnessElapsedSinceHovRunMs(state: WallState): number {
  if (state.activeWallId !== "freshness-wall") return 0;
  const elapsed = state.freshnessElapsedSinceHovRunMs;
  if (elapsed === null || !Number.isFinite(elapsed) || elapsed < 0) return 0;
  return elapsed;
}

export function getFreshnessWallRewardExponentBonus(
  state: WallState,
  capLevel: WallRewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
): number {
  return resolveAggregateWallRewardAddend(
    state,
    "mhov-reward-exponent-bonus",
    capLevel,
  );
}

export function isHgWallActive(state: WallState): boolean {
  return state.activeWallId === "hg-wall";
}

export function isStockWallActive(state: WallState): boolean {
  return state.activeWallId === "stock-wall";
}

export function resolveActiveWallAmplifierChallenge(
  state: WallState,
): WallAmplifierChallenge | null {
  if (state.activeWallId === null) return null;
  return getWallChallengePolicy(state.activeWallId).amplifierChallenge ?? null;
}

export function hasAmplificationWallClear(state: WallState): boolean {
  return state.amplificationWallBestClearMs !== null;
}

export function resolveAmplificationWallRaPropagationRewardEffect(
  state: WallState,
): WallAmplifierGainRaPropagationRewardEffect {
  const effect = AMPLIFICATION_WALL_CHALLENGE_POLICY.resolveRewardEffect(state);
  if (effect.dimension !== "amplifier-gain-ra-propagation-influence") {
    throw new Error("Amplification Wall returned an invalid reward dimension");
  }
  return effect;
}

export function getStockWallEffectiveNhovExecutionLimit(
  state: WallState,
): number {
  return resolveStockWallEffectiveNhovExecutionLimit(
    state.stockWallBestClearMs,
  );
}

export interface StockWallNhovExecutionStock {
  readonly usedCount: number;
  readonly effectiveLimit: number;
  readonly remainingCount: number;
  readonly depleted: boolean;
}

export function getStockWallNhovExecutionStock(
  state: WallState,
): StockWallNhovExecutionStock | null {
  if (!isStockWallActive(state)) return null;
  const effectiveLimit = getStockWallEffectiveNhovExecutionLimit(state);
  const usedCount = Math.max(0, state.stockWallNhovUsedCount);
  const remainingCount = Math.max(0, effectiveLimit - usedCount);
  return {
    usedCount,
    effectiveLimit,
    remainingCount,
    depleted: remainingCount === 0,
  };
}

export function isStockWallNhovExecutionDepleted(state: WallState): boolean {
  return getStockWallNhovExecutionStock(state)?.depleted === true;
}

export function hasStockWallFirstNhovBranchSelectionReward(
  state: WallState,
): boolean {
  return state.stockWallBestClearMs !== null;
}

// Permanent BHG -> HG power exponent bonus contributed by cleared HG Walls.
// Composes additively with the skill-side `hov-hg-exponent-bonus` on the
// model's effective exponent; returns 0 until the HG Wall is cleared.
export function getHgWallRewardExponentBonus(state: WallState): number {
  return resolveAggregateWallRewardAddend(
    state,
    "bhg-to-hg-power-exponent-bonus",
  );
}

// Number of distinct walls with a recorded clear. Used by the amplifier
// cleared-wall-kind bonus.
export function getClearedWallKindCount(state: WallState): number {
  let count = 0;
  for (const policy of WALL_CHALLENGE_POLICIES) {
    if (policy.hasClearRecord(state)) count++;
  }
  return count;
}
